import React, { useState, useCallback } from 'react';
import type {
  UserProfileFormData,
  UserProfileFormLabels,
  UserProfileFormProps,
} from '../../shared/types/userProfileForm';

export type { UserProfileFormData, UserProfileFormLabels, UserProfileFormProps };

export function UserProfileForm({
  initialData,
  onSubmit,
  onCancel,
  errors = {},
  labels: l,
  languageOptions,
  timeZoneOptions,
  isSubmitting = false,
}: UserProfileFormProps) {
  const [formData, setFormData] = useState<UserProfileFormData>(initialData);

  const handleChange = useCallback((field: keyof UserProfileFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Account Settings */}
      <h3 className="heading-tertiary mb-3">{l.accountSettings}</h3>

      <div className="mb-3">
        <label htmlFor="name" className="form-label fw-semibold">{l.name}</label>
        <input
          type="text"
          className="form-control"
          id="name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder={l.namePlaceholder}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="email" className="form-label fw-semibold">{l.email}</label>
        <input
          type="email"
          className="form-control"
          id="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="language" className="form-label fw-semibold">{l.language}</label>
        <select
          className="form-select"
          id="language"
          value={formData.language}
          onChange={(e) => handleChange('language', e.target.value)}
        >
          {languageOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label htmlFor="timeZone" className="form-label fw-semibold">{l.timeZone}</label>
        <select
          className="form-select"
          id="timeZone"
          value={formData.timeZone}
          onChange={(e) => handleChange('timeZone', e.target.value)}
        >
          {timeZoneOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Notification Settings */}
      <h3 className="heading-tertiary mt-4 mb-3">{l.notificationSettings}</h3>

      <div className="mb-3">
        <h5 className="fw-semibold mb-3">{l.emailNotifications}</h5>
        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="emailOnReports"
            checked={formData.emailOnReports}
            onChange={(e) => handleChange('emailOnReports', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="emailOnReports">{l.emailOnReports}</label>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="emailOnAchievements"
            checked={formData.emailOnAchievements}
            onChange={(e) => handleChange('emailOnAchievements', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="emailOnAchievements">{l.emailOnAchievements}</label>
        </div>
      </div>

      <div className="mb-4">
        <h5 className="fw-semibold mb-3">{l.pushNotifications}</h5>
        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="pushOnReports"
            checked={formData.pushOnReports}
            onChange={(e) => handleChange('pushOnReports', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="pushOnReports">{l.pushOnReports}</label>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="pushOnAchievements"
            checked={formData.pushOnAchievements}
            onChange={(e) => handleChange('pushOnAchievements', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="pushOnAchievements">{l.pushOnAchievements}</label>
        </div>
      </div>

      {/* Daily Reminder */}
      <h3 className="heading-tertiary mt-4 mb-3">{l.dailyReminder}</h3>

      <div className="mb-3">
        <div className="form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input"
            id="reminderEnabled"
            checked={formData.reminderEnabled}
            onChange={(e) => handleChange('reminderEnabled', e.target.checked)}
          />
          <label className="form-check-label fw-semibold" htmlFor="reminderEnabled">{l.reminderEnabled}</label>
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="reminderTime" className="form-label fw-semibold">{l.reminderTime}</label>
        <input
          type="time"
          className="form-control"
          id="reminderTime"
          value={formData.reminderTime}
          onChange={(e) => handleChange('reminderTime', e.target.value)}
        />
        <small className="form-text text-traq-muted">{l.reminderHelp}</small>
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-link text-traq-primary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn-primary-gradient"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default UserProfileForm;
